import {EncAlgorithm, Encryption, getEncryption} from '../utils/crypto';
import {LOCAL_STORAGE, SESSION_STORAGE} from "@core/constant/storage.constant";

export class InvalidSecretKeyError extends Error {
  constructor() {
    super(
      'The secretKey parameter must bne contains min 10 characters. Please provide a valid secretKey',
    );
    this.name = 'InvalidSecretKey';
  }
}
export interface EncryptStorageOptions {
  prefix?: string;
  stateManagementUse?: boolean;
  storageType?: 'localStorage' | 'sessionStorage';
  encAlgorithm?: EncAlgorithm;
}

export interface RemoveFromPatternOptions {
  exact?: boolean;
}

export interface GetFromPatternOptions extends RemoveFromPatternOptions {
  multiple?: boolean;
}

export interface EncryptStorageTypes extends Storage {

  setItem(key: string, value: any): void;


  getItem(key: string): string | any | undefined;


  removeItem(key: string): void;


  getItemFromPattern(
    pattern: string,
    options?: GetFromPatternOptions,
  ): Record<string, any> | any | undefined;


  removeItemFromPattern(
    pattern: string,
    options?: RemoveFromPatternOptions,
  ): void;


  clear(): void;


  key(index: number): string | null;


  encryptString(key: string): string;


  decryptString(key: string): string;
}

const secret = new WeakMap();

export class EncryptStorage implements EncryptStorageTypes {
  private readonly encriptation: Encryption;

  private readonly storage: Storage;

  private readonly prefix: string;

  private readonly stateManagementUse: boolean;

  constructor(secretKey: string, options?: EncryptStorageOptions) {
    if (secretKey.length < 10) {
      throw new InvalidSecretKeyError();
    }

    const {
      storageType = 'localStorage',
      prefix = '',
      stateManagementUse = false,
      encAlgorithm = 'AES',
    } = options || {};

    secret.set(this, secretKey);

    this.storage = window[storageType];
    this.prefix = prefix;
    this.stateManagementUse = stateManagementUse;
    this.encriptation = getEncryption(encAlgorithm, secret.get(this));
  }

  private getKey(key: string): string {
    return this.prefix ? `${this.prefix}:${key}` : key;
  }

  public get length() {
    return this.storage.length || 0;
  }

  public setItem(key: string, value: any): void {
    const storageKey = this.getKey(key);
    const valueToString =
      typeof value === 'object' ? JSON.stringify(value) : String(value);
    const encryptedValue = this.encriptation.encrypt(valueToString);

    this.storage.setItem(storageKey, encryptedValue);
  }

  public getItem<T = any>(key: string): T | undefined {
    const storageKey = this.getKey(key);
    const item = this.storage.getItem(storageKey);
    if (item) {
      const decryptedValue = this.encriptation.decrypt(item);

      if (this.stateManagementUse) {
        return decryptedValue as unknown as T;
      }

      try {
        return JSON.parse(decryptedValue) as T;
      } catch (error) {
        return decryptedValue as unknown as T;
      }
    }

    return undefined;
  }

  public removeItem(key: string): void {
    const storageKey = this.getKey(key);
    this.storage.removeItem(storageKey);
  }

  public removeItemFromPattern(
    pattern: string,
    options: RemoveFromPatternOptions = {} as RemoveFromPatternOptions,
  ): void {
    const { exact = false } = options;
    const storageKeys = Object.keys(this.storage);
    const filteredKeys = storageKeys.filter(key => {
      if (exact) {
        return key === this.getKey(pattern);
      }

      if (this.prefix) {
        return key.includes(pattern) && key.includes(this.prefix);
      }

      return key.includes(pattern);
    });

    filteredKeys.forEach(key => {
      this.storage.removeItem(key);
    });
  }

  public getItemFromPattern(
    pattern: string,
    options: GetFromPatternOptions = {} as GetFromPatternOptions,
  ): Record<string, any> | undefined {
    const { multiple = true, exact = false } = options;
    const keys = Object.keys(this.storage).filter(key => {
      if (exact) {
        return key === this.getKey(pattern);
      }

      if (this.prefix) {
        return key.includes(pattern) && key.includes(this.prefix);
      }

      return key.includes(pattern);
    });

    if (!keys.length) {
      return undefined;
    }

    if (!multiple) {
      const [key] = keys;

      const formattedKey = this.prefix
        ? key.replace(`${this.prefix}:`, '')
        : key;

      return this.getItem(formattedKey);
    }

    return keys.reduce((accumulator: Record<string, any>, key) => {
      const formattedKey = this.prefix
        ? key.replace(`${this.prefix}:`, '')
        : key;

      accumulator[formattedKey] = this.getItem(formattedKey);

      return accumulator;
    }, {});
  }

  public clear(): void {
    this.storage.clear();
  }

  public key(index: number): string | null {
    return this.storage.key(index) || null;
  }

  public encryptString(str: string): string {
    return this.encriptation.encrypt(str);
  }

  public decryptString(str: string): string {
    return this.encriptation.decrypt(str);
  }
}

export default EncryptStorage;
